import { Region } from "@api/getregions";
import { getUserProfile, UserDetails } from "@api/getuserProfile";
import React, { createContext, useContext, useEffect, useState } from "react";
import { VwpCategory, VwpOrderCategory, VwpReview, VwpSupplierPackage } from "./types";
import { addQueryParamNoRefresh, constructFullUrl } from "@helper/urlHelpers";
import { getUnavailableSuppliers } from "@api/getUnavailableSuppliers";
import { formatDate } from "@helper/dateHelper";
import NotificationProvider from "@components/Notification";
import { storeVwpOrder } from "@api/storeVwpOrder";
import { CART_COOKIE_COUNT, CART_COOKIE_NAME, THREE_MONTH_IN_SECONDS } from "@helper/constants";
import { setCookie } from "@helper/cookies";
import { getVwpCart } from "@api/getVwpCart";
import { ga4Event } from "@helper/gtm";

interface VwpContextValue {
    vwpCategories: VwpCategory[];
    vwpReviews?: VwpReview[];
    initialRegionId: number | null;
    facebookAppId?: string;
    enableGTMTracking: boolean;
    region: Region | null;
    weddingDate: Date | null;
    filterModalPackageId: number | null;
    userDetails?: UserDetails;
    suppliersToHide: number[];
    selectedPackageId: number | null;
    isLoading: boolean;
    setRegion: React.Dispatch<React.SetStateAction<Region | null>>;
    setWeddingDate: React.Dispatch<React.SetStateAction<Date | null>>;
    setFilterModalPackageId: React.Dispatch<React.SetStateAction<number | null>>;
    setSelectedPackageId: React.Dispatch<React.SetStateAction<number | null>>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    onLoggedIn: () => void;
    onPackageSelected: (packageId: number) => void;
    findSupplierByPackageId: (packageId: number) => VwpSupplierPackage | null;
    saveOrder: (order: VwpOrderCategory, redeem: boolean) => Promise<boolean>;
    getRedeemWedPoints: () => Promise<boolean>;
}

interface ProviderProps {
    regionId?: number;
    facebookAppId?: string;
    enableGTMTracking: boolean;
    vwpCategories: VwpCategory[];
    vwpReviews?: VwpReview[];
    children: React.ReactNode;
}

const VwpContext = createContext<VwpContextValue | undefined>(
    undefined
);

export const VwpProvider = ({ regionId, facebookAppId, enableGTMTracking, vwpCategories, vwpReviews, children }: ProviderProps) => {
    const [initialRegionId, setInitialRegionId] = useState<number | null>(!!regionId ? regionId : null);
    const [region, setRegion] = useState<Region | null>(null);
    const [weddingDate, setWeddingDate] = useState<Date | null>(null);
    const [filterModalPackageId, setFilterModalPackageId] = useState<number | null>(null);
    const [userDetails, setUserDetails] = useState<UserDetails>();
    const [suppliersToHide, setSuppliersToHide] = useState<number[]>([]);
    const [selectedPackageId, setSelectedPackageId] = useState<number | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    useEffect(() => {
        initData();
    }, []);

    useEffect(() => {
        if (!!region) {
            ga4Event('packages_region_set', { region: region.Name }, enableGTMTracking);

            if (!initialRegionId) {
                redirectToRegion(region.Slug);
            }
            else if (initialRegionId !== region.Id) {
                redirectToRegion(region.Slug);
            }
        }
    }, [region, initialRegionId]);

    useEffect(() => {
        if (!!weddingDate) {
            getUnavailableSuppliers(weddingDate).then(result => {
                if (!!result && result.length > 0) {
                    setSuppliersToHide(result);
                }
                else {
                    setSuppliersToHide([]);
                }
                showSelectedPackage();
            });
            addQueryParamNoRefresh('date', formatDate(weddingDate));
        }
        else {
            setSuppliersToHide([]);
        }
    }, [weddingDate]);

    const onLoggedIn = () => {
        initData();
    };

    const initData = async () => {
        let date: Date | null = null;

        if (!!window) {
            const params = new URLSearchParams(window.location.search);
            const weddingDateParam = params.get('date');
            const packageId = params.get('packageId');

            if (!!weddingDateParam) {
                date = new Date(weddingDateParam);
                setWeddingDate(date);
            }

            if (!!packageId) {
                setSelectedPackageId(parseInt(packageId));
            }
        }

        getUserProfile().then(result => {
            if (!!result) {
                // According to ANG-9609 we don't need to set the initial region id, can put it back if needed
                // setInitialRegionId(!!result.RegionId ? result.RegionId : null);

                if (!date) {
                    setWeddingDate(result.WeddingDate ? new Date(result.WeddingDate) : null);
                }
                setUserDetails(result);
            }
        });
    };

    const getRedeemWedPoints = async () => {
        return getVwpCart()
            .then(result => {
                if (!!result && result.categories.length > 0) {
                    return result.redeemPoints;
                }

                return false;
            })
            .catch(() => {
                console.log('Error getting RedeemWedPoints');

                return false;
            });
    };

    const redirectToRegion = (slug: string) => {
        setIsLoading(true);

        const weddingQuery = !!weddingDate ? `date=${formatDate(weddingDate)}` : '';
        const packageQuery = !!filterModalPackageId ? `packageId=${filterModalPackageId}` : '';
        const query = (!!weddingQuery && !!packageQuery) ? `?${weddingQuery}&${packageQuery}` : (!!weddingQuery ? `?${weddingQuery}` : (!!packageQuery ? `?${packageQuery}` : ''));
        const url = constructFullUrl(`/packages/location/${slug}/${query}`);

        if (window.location.href !== url) {
            window.location.href = url;
        }
    };

    const showSelectedPackage = () => {
        if (!!filterModalPackageId && !!region && !!weddingDate) {
            setSelectedPackageId(filterModalPackageId);
            setFilterModalPackageId(null);
        }
    };

    const onPackageSelected = (packageId: number) => {
        if (!!region && !!weddingDate) {
            setSelectedPackageId(packageId);
            addQueryParamNoRefresh('packageId', packageId.toString());
        }
        else {
            setFilterModalPackageId(packageId);
        }
    };

    const findSupplierByPackageId = (packageId: number): VwpSupplierPackage | null => {
        for (const cat of vwpCategories) {
          const pkg = cat.Packages.find(p => p.Id === packageId || p.OtherSupplierPackageItemIds?.includes(packageId));

          if (!!pkg) {
            const catId = Number(cat.Id);
            if (isNaN(catId)) {
              console.error('Invalid ID:', cat.Id);
            }

            return {
                supplierId: pkg.SupplierId,
                categoryId: catId,
                categorySlug: cat.Slug,
                packageIds: [...(!!pkg.OtherSupplierPackageItemIds ? pkg.OtherSupplierPackageItemIds : []), pkg.Id],
            };
          }
        }

        return null;
    };

    const saveOrder = async (order: VwpOrderCategory, redeem: boolean): Promise<boolean> => {
        const result = await storeVwpOrder(order, redeem, !!weddingDate ? formatDate(weddingDate) : '');

        if (!!result) {
            setCookie(CART_COOKIE_NAME, result.key, THREE_MONTH_IN_SECONDS);
            setCookie(CART_COOKIE_COUNT, result.value.toString(), THREE_MONTH_IN_SECONDS);
            return true;
        }
        return false;
    }

    return (
        <NotificationProvider>
            <VwpContext.Provider value={{
                vwpCategories,
                vwpReviews,
                initialRegionId,
                facebookAppId,
                enableGTMTracking,
                region,
                weddingDate,
                filterModalPackageId,
                userDetails,
                suppliersToHide,
                selectedPackageId,
                isLoading,
                setRegion,
                setWeddingDate,
                setFilterModalPackageId,
                setSelectedPackageId,
                setIsLoading,
                onLoggedIn,
                onPackageSelected,
                findSupplierByPackageId,
                saveOrder,
                getRedeemWedPoints
            }}>
                {children}
            </VwpContext.Provider>
        </NotificationProvider>
    );
}

export const useVwp = () => {
    const vwpContext = useContext(VwpContext);

    if (!vwpContext) {
        throw new Error("useVwp must be used within an vwpContextProvider");
    }

    return vwpContext;
};
